import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    useFantasyModalBackGuard
} from "./useFantasyModalBackGuard";
import {
    americanFootballOutline,
    closeOutline,
    peopleOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";



import {
    GameDetail,
    GamePlay,
    PlayersById,
    Team,
    getGameDetail,
    getPlayers,
    getTeams
} from "../services/mexfbaApi";

import {
    calculateFantasyMatchPlayers,
    FantasyPlayerResult,
    FantasyRosterPlayer,
    FantasySettings,
    RealMatch,
    RealPlay
} from "../services/fantasyGameCalculator";



import {
    createGameTimeline,
    getPlayDescription,
    getPlayType,
    TimelinePlay
} from "../utils/gamePlayTimeline";

import "./FantasyMatchModal.css";

interface FantasyMatchModalProps {
    isOpen: boolean;
    match: RealMatch | null;
    fantasySettings: FantasySettings;
    onDismiss: () => void;
}

type ModalTab = "plays" | "players";

interface FantasyTeamPlayers {
    id: string;
    name: string;
    playersByPosition: Record<
        string,
        FantasyPlayerResult[]
    >;
}

const fantasyPositionOrder = [
    "QB",
    "RB",
    "WR",
    "K",
    "DEF"
];

function getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
        scheduled: "Programado",
        live: "En vivo",
        final: "Final",
        postponed: "Pospuesto",
        today: "Hoy"
    };

    return labels[status] || "Pendiente";
}

function getStatusClass(status: string): string {
    const classes: Record<string, string> = {
        scheduled: "fantasy-match-status--scheduled",
        live: "fantasy-match-status--live",
        final: "fantasy-match-status--final",
        postponed: "fantasy-match-status--postponed",
        today: "fantasy-match-status--today"
    };

    return classes[status] ||
        "fantasy-match-status--scheduled";
}

function formatPoints(points: number): string {
    return points.toFixed(2);
}
function getFantasyStatDetails(
    player: FantasyPlayerResult
): string {
    if (player.position === "QB") {
        const stats = player.stats as {
            ydsP: number;
            tdP: number;
            int: number;
            fum: number;
            fumL: number;
        };

        return [
            `${stats.ydsP} YDS`,
            `${stats.tdP} TD`,
            `${stats.int} INT`,
            stats.fumL > 0
                ? `${stats.fumL} FL`
                : ""
        ]
            .filter(Boolean)
            .join(" · ");
    }

    if (player.position === "RB") {
        const stats = player.stats as {
            ydsR: number;
            tdR: number;
            rec: number;
            ydsRec: number;
            tdRec: number;
            fumL: number;
        };

        const totalTouchdowns =
            stats.tdR + stats.tdRec;

        return [
            `${stats.ydsR} RYDS`,
            `${stats.rec} REC`,
            `${stats.ydsRec} REYDS`,
            `${totalTouchdowns} TD`,
            stats.fumL > 0
                ? `${stats.fumL} FL`
                : ""
        ]
            .filter(Boolean)
            .join(" · ");
    }

    if (player.position === "WR") {
        const stats = player.stats as {
            rec: number;
            ydsRec: number;
            tdRec: number;
            tdRet: number;
            fumL: number;
        };

        const totalTouchdowns =
            stats.tdRec + stats.tdRet;

        return [
            `${stats.rec} REC`,
            `${stats.ydsRec} YDS`,
            `${totalTouchdowns} TD`,
            stats.fumL > 0
                ? `${stats.fumL} FL`
                : ""
        ]
            .filter(Boolean)
            .join(" · ");
    }

    if (player.position === "K") {
        const stats = player.stats as {
            pat: number;
            fg20: number;
            fg30: number;
            fg40: number;
            fg50: number;
            missed: number;
        };

        const fieldGoals =
            stats.fg20 +
            stats.fg30 +
            stats.fg40 +
            stats.fg50;

        return [
            `${fieldGoals} FG`,
            `${stats.pat} XP`,
            stats.missed > 0
                ? `${stats.missed} ERR`
                : ""
        ]
            .filter(Boolean)
            .join(" · ");
    }

    if (player.position === "DEF") {
        const stats = player.stats as {
            sack: number;
            int: number;
            fum: number;
            fumR: number;
            saf: number;
            td: number;
            ydsAgainst: number;
        };

        return [
            `${stats.sack} SCK`,
            `${stats.int} INT`,
            `${stats.fum} FF`,
            `${stats.fumR} FR`,
            stats.saf > 0
                ? `${stats.saf} SFTY`
                : "",
            stats.td > 0
                ? `${stats.td} TD`
                : "",
            `${stats.ydsAgainst} YDS C`
        ]
            .filter(Boolean)
            .join(" · ");
    }

    return player.statLine;
}

function getPlayerPositionId(player: {
    posicion?: string;
    pos?: string;
}): string {
    return String(
        player.posicion ||
        player.pos ||
        ""
    );
}

function getPlayerTeamId(player: {
    equipo_id?: string;
    teamId?: string;
}): string {
    return String(
        player.equipo_id ||
        player.teamId ||
        ""
    );
}

function getPlayerName(player: {
    nombre?: string;
    name?: string;
}): string {
    return String(
        player.nombre ||
        player.name ||
        "Jugador"
    );
}

function getPositionName(positionId: string): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "7": "K",
        "8": "DEF"
    };

    return positions[positionId] || "—";
}

function getPositionOrder(position: string): number {
    const index = fantasyPositionOrder.indexOf(position);

    return index === -1
        ? 99
        : index;
}

function getPlayedPlayerIds(
    plays: RealPlay[]
): Set<string> {
    const ids = new Set<string>();

    plays.forEach(function (play) {
        [
            play.QB,
            play.RB,
            play.WR,
            play.K,
            play.ret
        ].forEach(function (playerId) {
            const id = String(playerId || "").trim();

            if (id) {
                ids.add(id);
            }
        });
    });

    return ids;
}
function normalizeMatchStatus(
    value: unknown
): string {
    const status = String(value || "")
        .trim()
        .toLowerCase();

    const statusMap: Record<string, string> = {
        scheduled: "scheduled",
        programado: "scheduled",
        pendiente: "scheduled",

        today: "today",
        hoy: "today",

        live: "live",
        "en vivo": "live",
        en_vivo: "live",
        en_curso: "live",
        "en curso": "live",

        final: "final",
        finalizado: "final",
        terminado: "final",

        postponed: "postponed",
        pospuesto: "postponed",
        aplazado: "postponed"
    };

    return statusMap[status] || "";
}

function getEffectiveMatchStatus(
    gameStatus: unknown,
    fallbackStatus: string,
    playsCount: number
): string {
    const explicitStatus = normalizeMatchStatus(
        gameStatus
    );

    if (explicitStatus) {
        return explicitStatus;
    }

    const controllerStatus = normalizeMatchStatus(
        fallbackStatus
    );

    if (controllerStatus) {
        return controllerStatus;
    }

    /*
        Si no hay estado explícito, pero ya existen jugadas,
        el partido está activo de forma inferida.
    */
    if (playsCount > 0) {
        return "live";
    }

    return "scheduled";
}

function createScoringMatch(
    match: RealMatch,
    detail: GameDetail
): RealMatch {
    /*
        El tipo GameDetail puede no declarar campos agregados como
        estado, hora o resLocal. Se normaliza para conservar
        compatibilidad con archivos históricos y actuales.
    */
const data = (
    detail.Data ?? {}
) as unknown as Record<string, unknown>;


    const detailPlays = detail.Jugadas
        ? (
            Array.isArray(detail.Jugadas)
                ? detail.Jugadas
                : Object.values(detail.Jugadas)
        )
        : [];

    const status = getEffectiveMatchStatus(
        data.estado,
        match.status,
        detailPlays.length
    );

    return {
        id: match.id,
        status,

        date: String(
            data.fecha ||
            match.date ||
            ""
        ),

        hour: String(
            data.hora ||
            match.hour ||
            ""
        ),

        local: {
            id: String(
                data.id_local ||
                match.local.id ||
                ""
            ),

            name: String(
                data.nombre_local ||
                match.local.name ||
                ""
            ),

            score: String(
                data.resLocal ??
                match.local.score ??
                "0"
            )
        },

        visitor: {
            id: String(
                data.id_visita ||
                match.visitor.id ||
                ""
            ),

            name: String(
                data.nombre_visita ||
                match.visitor.name ||
                ""
            ),

            score: String(
                data.resVisita ??
                match.visitor.score ??
                "0"
            )
        },

        plays: detailPlays as RealPlay[]
    };
}


function createTemporaryRoster(
    players: PlayersById,
    match: RealMatch
): FantasyRosterPlayer[] {
    const validPositionIds = new Set([
        "1",
        "2",
        "3",
        "7"
    ]);

    const teamIds = new Set([
        match.local.id,
        match.visitor.id
    ]);

    const roster = Object.values(players)
        .filter(function (player) {
            const teamId = getPlayerTeamId(player);

            return (
                teamIds.has(teamId) &&
                validPositionIds.has(
                    getPlayerPositionId(player)
                )
            );
        })
        .map(function (player): FantasyRosterPlayer {
            return {
                id: String(player.id),
                name: getPlayerName(player),
                positionId: getPlayerPositionId(player),
                teamId: getPlayerTeamId(player),
                teamName: "",
                fantasySlot: getPositionName(
                    getPlayerPositionId(player)
                )
            };
        });

    /*
        DEF usa como id el id del equipo real. Esto coincide con
        getDefenseStats() y con la estructura usada por el cálculo.
    */
    roster.push(
        {
            id: match.local.id,
            name: `${match.local.name} DEF`,
            positionId: "8",
            teamId: match.local.id,
            teamName: match.local.name,
            fantasySlot: "DEF"
        },
        {
            id: match.visitor.id,
            name: `${match.visitor.name} DEF`,
            positionId: "8",
            teamId: match.visitor.id,
            teamName: match.visitor.name,
            fantasySlot: "DEF"
        }
    );

    return roster;
}

function getTeamPlayers(
    teamId: string,
    teamName: string,
    allPlayers: FantasyPlayerResult[],
    playedPlayerIds: Set<string>
): FantasyTeamPlayers {
    const playersByPosition: Record<
        string,
        FantasyPlayerResult[]
    > = {
        QB: [],
        RB: [],
        WR: [],
        K: [],
        DEF: []
    };

    allPlayers
        .filter(function (player) {
            /*
                La defensa se incluye siempre porque representa
                al equipo, incluso si todavía no hay jugadas.
            */
            if (player.positionId === "8") {
                return player.teamId === teamId;
            }

            return (
                player.teamId === teamId &&
                playedPlayerIds.has(player.id)
            );
        })
        .forEach(function (player) {
            if (!playersByPosition[player.position]) {
                return;
            }

            playersByPosition[player.position].push(
                player
            );
        });

    fantasyPositionOrder.forEach(function (position) {
        playersByPosition[position].sort(
            function (left, right) {
                const pointsDifference =
                    right.points - left.points;

                if (pointsDifference !== 0) {
                    return pointsDifference;
                }

                return left.name.localeCompare(
                    right.name,
                    "es-MX"
                );
            }
        );
    });

    return {
        id: teamId,
        name: teamName,
        playersByPosition
    };
}

const FantasyMatchModal: FC<
    FantasyMatchModalProps
> = function ({

    isOpen,
    match,
    fantasySettings,
    onDismiss
}) {
    const [activeTab, setActiveTab] =
        useState<ModalTab>("plays");

    const [gameDetail, setGameDetail] =
        useState<GameDetail | null>(null);

    const [players, setPlayers] =
        useState<PlayersById>({});

    const [teams, setTeams] = useState<Team[]>([]);


    const [activeQuarter, setActiveQuarter] =
        useState("todos");

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    useEffect(
    function () {
        isRefreshingRef.current = refreshing;
    },
    [refreshing]
);

    const [error, setError] = useState("");
    const requestVersionRef = useRef(0);
    /*
    El catálogo de jugadores y equipos no cambia por cada partido.
    Se carga una sola vez mientras el componente esté montado.
*/
const hasCatalogRef = useRef(false);
const isRefreshingRef = useRef(false);


const loadMatch = useCallback(
    async function (forceRefresh = false) {
        if (!match?.id) {
            return;
        }

        const requestVersion =
            requestVersionRef.current + 1;

        requestVersionRef.current = requestVersion;

        const mustLoadCatalog =
            !hasCatalogRef.current;

        try {
            setError("");

            if (forceRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const detailRequest = getGameDetail(
                match.id,
                forceRefresh
            );

            /*
                Jugadores y equipos se descargan sólo la primera vez.
                En las actualizaciones posteriores solamente cambia
                el detalle del encuentro: jugadas, marcador y estado.
            */
            const catalogRequest = mustLoadCatalog
                ? Promise.all([
                    getPlayers(forceRefresh),
                    getTeams(forceRefresh)
                ])
                : Promise.resolve(null);

            const [
                detail,
                catalog
            ] = await Promise.all([
                detailRequest,
                catalogRequest
            ]);

            /*
                Evita que una petición de un partido anterior reemplace
                la información del partido que el usuario abrió después.
            */
            if (
                requestVersion !==
                requestVersionRef.current
            ) {
                return;
            }

            setGameDetail(detail);

            if (catalog) {
                const [
                    playersData,
                    teamsData
                ] = catalog;

                setPlayers(playersData);
                setTeams(teamsData);

                hasCatalogRef.current = true;
            }
        } catch (requestError) {
            if (
                requestVersion !==
                requestVersionRef.current
            ) {
                return;
            }

            console.error(
                "No fue posible cargar el partido Fantasy:",
                requestError
            );

            setError(
                "No fue posible cargar este partido."
            );
        } finally {
            if (
                requestVersion ===
                requestVersionRef.current
            ) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    },
    [match?.id]
);

        const scoringMatch = useMemo(
        function (): RealMatch | null {
            if (!match) {
                return null;
            }

            if (!gameDetail) {
                return match;
            }

            return createScoringMatch(
                match,
                gameDetail
            );
        },
        [match, gameDetail]
    );
        useEffect(
        function () {
            if (!isOpen || !match) {
                return;
            }

setActiveTab("plays");
setActiveQuarter("todos");
setError("");
setGameDetail(null);

loadMatch();


        },
        [isOpen, match?.id, loadMatch]

    );
useEffect(
    function () {
        if (!isOpen || !scoringMatch) {
            return;
        }

        const pollingTimes: Record<string, number> = {
            live: 30 * 1000,
            today: 60 * 1000,
            scheduled: 60 * 1000
        };

        const intervalMilliseconds =
            pollingTimes[scoringMatch.status];

        if (!intervalMilliseconds) {
            return;
        }

        const intervalId = window.setInterval(
            function () {
if (
    document.visibilityState !== "visible" ||
    isRefreshingRef.current
) {
    return;
}


                loadMatch(true);
            },
            intervalMilliseconds
        );

        return function () {
            window.clearInterval(intervalId);
        };
    },
    [
        isOpen,
        scoringMatch?.status,
        loadMatch
    ]
);

    const plays = useMemo(function (): GamePlay[] {
        if (!gameDetail?.Jugadas) {
            return [];
        }

        return Array.isArray(gameDetail.Jugadas)
            ? gameDetail.Jugadas
            : Object.values(gameDetail.Jugadas);
    }, [gameDetail]);

    const timeline = useMemo(
        function (): TimelinePlay[] {
            if (!gameDetail) {
                return [];
            }

            return createGameTimeline(
                gameDetail,
                plays
            );
        },
        [gameDetail, plays]
    );

    const quarters = useMemo(function (): string[] {
        return Array.from(
            new Set(
                plays
                    .map(function (play) {
                        return String(play.q || "");
                    })
                    .filter(Boolean)
            )
        ).sort(function (left, right) {
            return Number(left) - Number(right);
        });
    }, [plays]);

    const filteredTimeline = useMemo(
        function () {
            if (activeQuarter === "todos") {
                return timeline;
            }

            return timeline.filter(function (item) {
                return item.play.q === activeQuarter;
            });
        },
        [activeQuarter, timeline]
    );

    const teamSummaries = useMemo(
        function (): FantasyTeamPlayers[] {
            if (!scoringMatch) {
                return [];
            }

            const temporaryRoster = createTemporaryRoster(
                players,
                scoringMatch
            );

            const matchPlayers =
    calculateFantasyMatchPlayers(
        fantasySettings,
        temporaryRoster,
        scoringMatch
    );

const playedPlayerIds = getPlayedPlayerIds(
    scoringMatch.plays
);

return [
    getTeamPlayers(
        scoringMatch.local.id,
        scoringMatch.local.name,
        matchPlayers,
        playedPlayerIds
    ),

    getTeamPlayers(
        scoringMatch.visitor.id,
        scoringMatch.visitor.name,
        matchPlayers,
        playedPlayerIds
    )
];

        },
        [scoringMatch, players, fantasySettings]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [
                    String(team.id),
                    team
                ];
            })
        );
    }, [teams]);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadMatch(true);

        event.detail.complete();
    }

function resetModalState(): void {
    /*
        Invalida solicitudes pendientes del partido actual.
    */
    requestVersionRef.current += 1;

    setGameDetail(null);
    setError("");
    setLoading(false);
    setRefreshing(false);
}

function handleDidDismiss(): void {
    resetModalState();
    onDismiss();
}

function handleClose(): void {
    onDismiss();
}

useFantasyModalBackGuard({
    isOpen,
    onDismiss: handleClose,
    priority: 10000
});

    return (
<IonModal
    isOpen={isOpen}
    onDidDismiss={handleDidDismiss}
    className="fantasy-match-modal"
>
            <IonHeader translucent>
                <IonToolbar
                    className="fantasy-match-modal__toolbar"
                >
                    <IonButtons slot="start">
                        <IonButton
    aria-label="Cerrar partido"
    onClick={handleClose}
>

                            <IonIcon icon={closeOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Partido Fantasy</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar partido"
                            disabled={loading || refreshing}
                            onClick={function () {
                                loadMatch(true);
                            }}
                        >
                            {refreshing ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <IonIcon
                                    icon={refreshOutline}
                                />
                            )}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="fantasy-match-modal__content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando partido..."
                    />
                </IonRefresher>

                {loading && !gameDetail ? (
                    <IonProgressBar type="indeterminate" />
                ) : null}

                {error ? (
                    <main className="fantasy-match-modal__empty">
                        <IonIcon icon={statsChartOutline} />

                        <strong>
                            No fue posible cargar el partido
                        </strong>

                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadMatch(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </main>
                ) : null}

                {!error && scoringMatch ? (
                    <>
                        <section
                            className="fantasy-match-modal__score"
                        >
                            <div>
                                <strong>
                                    {scoringMatch.local.name}
                                </strong>

                                <span>
                                    {scoringMatch.local.score || "0"}
                                </span>
                            </div>

                            <section>
                                <IonBadge
                                    className={getStatusClass(
                                        scoringMatch.status
                                    )}
                                >
                                    {getStatusLabel(
                                        scoringMatch.status
                                    )}
                                </IonBadge>

                                <strong>VS</strong>
                            </section>

                            <div>
                                <strong>
                                    {scoringMatch.visitor.name}
                                </strong>

                                <span>
                                    {scoringMatch.visitor.score || "0"}
                                </span>
                            </div>
                        </section>

                        <IonSegment
                            className="fantasy-match-modal__segment"
                            value={activeTab}
                            onIonChange={function (event) {
                                setActiveTab(
                                    event.detail.value as ModalTab
                                );
                            }}
                        >
                            <IonSegmentButton value="plays">
                                Jugadas
                            </IonSegmentButton>

                            <IonSegmentButton value="players">
                                Jugadores
                            </IonSegmentButton>
                        </IonSegment>

                        {activeTab === "plays" ? (
                            <>
                                <section
                                    className="fantasy-match-modal__quarters"
                                >
                                    <IonButton
                                        fill={
                                            activeQuarter === "todos"
                                                ? "solid"
                                                : "outline"
                                        }
                                        onClick={function () {
                                            setActiveQuarter("todos");
                                        }}
                                    >
                                        Todos
                                    </IonButton>

                                    {quarters.map(function (quarter) {
                                        return (
                                            <IonButton
                                                key={quarter}
                                                fill={
                                                    activeQuarter === quarter
                                                        ? "solid"
                                                        : "outline"
                                                }
                                                onClick={function () {
                                                    setActiveQuarter(
                                                        quarter
                                                    );
                                                }}
                                            >
                                                {quarter}Q
                                            </IonButton>
                                        );
                                    })}
                                </section>

                                <section
                                    className="fantasy-match-modal__section"
                                >
                                    <div
                                        className="fantasy-match-modal__heading"
                                    >
                                        <div>
                                            <span>CRONOLOGÍA</span>

                                            <h2>
                                                {activeQuarter === "todos"
                                                    ? "Todas las jugadas"
                                                    : `Cuarto ${activeQuarter}`}
                                            </h2>
                                        </div>

                                        <IonIcon
                                            icon={statsChartOutline}
                                        />
                                    </div>

                                    {filteredTimeline.length === 0 ? (
                                        <div
                                            className="fantasy-match-modal__no-results"
                                        >
                                            No hay jugadas registradas.
                                        </div>
                                    ) : (
                                        filteredTimeline.map(
                                            function (item) {
                                                const team =
                                                    teamsById.get(
                                                        String(
                                                            item.play.ofensiva
                                                        )
                                                    );

                                                return (
                                                    <article
                                                        className={[
                                                            "fantasy-match-play",
                                                            item.scoringPoints > 0
                                                                ? "fantasy-match-play--score"
                                                                : ""
                                                        ].join(" ")}
                                                        key={`${item.index}-${item.play.q}`}
                                                    >
                                                        {item.down ? (
                                                            <span
                                                                className="fantasy-match-play__down"
                                                            >
                                                                {item.down}
                                                            </span>
                                                        ) : null}

                                                        <div
                                                            className="fantasy-match-play__top"
                                                        >
                                                            <IonBadge>
                                                                {item.play.q
                                                                    ? `${item.play.q}Q`
                                                                    : "—"}
                                                            </IonBadge>

                                                            <span>
                                                                {getPlayType(
                                                                    item.play
                                                                )}
                                                            </span>

                                                            <strong>
                                                                {item.localScore}-
                                                                {item.visitorScore}
                                                            </strong>
                                                        </div>

                                                        <div
                                                            className="fantasy-match-play__body"
                                                        >
                                                            {team?.logo ? (
                                                                <img
                                                                    src={team.logo}
                                                                    alt=""
                                                                />
                                                            ) : (
                                                                <IonIcon
                                                                    icon={
                                                                        americanFootballOutline
                                                                    }
                                                                />
                                                            )}

                                                            <p>
                                                                {getPlayDescription(
                                                                    item.play,
                                                                    players
                                                                )}
                                                            </p>
                                                        </div>

                                                        {item.scoringPoints > 0 ? (
                                                            <div
                                                                className="fantasy-match-play__score"
                                                            >
                                                                +
                                                                {
                                                                    item.scoringPoints
                                                                }{" "}
                                                                puntos
                                                            </div>
                                                        ) : null}
                                                    </article>
                                                );
                                            }
                                        )
                                    )}
                                </section>
                            </>
                        ) : (
                            <section
                                className="fantasy-match-modal__section"
                            >
                                <div
                                    className="fantasy-match-modal__heading"
                                >
                                    <div>
                                        <span>
                                            RESUMEN FANTASY
                                        </span>

                                        <h2>
                                            Jugadores del partido
                                        </h2>
                                    </div>

                                    <IonIcon icon={peopleOutline} />
                                </div>

                                {teamSummaries.map(function (team) {
                                    return (
                                        <section
                                            className="fantasy-match-team"
                                            key={team.id}
                                        >
                                            <header>
                                                <span>
                                                    EQUIPO
                                                </span>

                                                <h3>{team.name}</h3>
                                            </header>

                                            {fantasyPositionOrder.map(
                                                function (position) {
                                                    const teamPlayers =
                                                        team.playersByPosition[
                                                            position
                                                        ] || [];

                                                    if (
                                                        teamPlayers.length ===
                                                        0
                                                    ) {
                                                        return null;
                                                    }

                                                    return (
                                                        <section
                                                            className="fantasy-match-position"
                                                            key={position}
                                                        >
                                                            <h4>
                                                                {position}
                                                            </h4>
<div className="fantasy-match-player__headings">
    <span />
    <span>JUGADOR / ESTADÍSTICAS</span>
    <span>FANTASY</span>
</div>

                                                            {teamPlayers.map(
                                                                function (
                                                                    player
                                                                ) {
                                                                    return (
                                                                        <article
                                                                            className="fantasy-match-player"
                                                                            key={
                                                                                player.id
                                                                            }
                                                                        >
                                                                            <span
                                                                                className="fantasy-match-player__position"
                                                                            >
                                                                                {
                                                                                    player.position
                                                                                }
                                                                            </span>

                                                                            <div
                                                                                className="fantasy-match-player__main"
                                                                            >
                                                                                <strong>
                                                                                    {
                                                                                        player.name
                                                                                    }
                                                                                </strong>

                                                                                <small>
    {getFantasyStatDetails(player)}
</small>

                                                                            </div>

                                                                            <div
                                                                                className="fantasy-match-player__points"
                                                                            >
                                                                                <strong>
                                                                                    {formatPoints(
                                                                                        player.points
                                                                                    )}
                                                                                </strong>

                                                                                <span>
                                                                                    PTS
                                                                                </span>
                                                                            </div>
                                                                        </article>
                                                                    );
                                                                }
                                                            )}
                                                        </section>
                                                    );
                                                }
                                            )}

                                            {fantasyPositionOrder.every(
                                                function (position) {
                                                    return (
                                                        team.playersByPosition[
                                                            position
                                                        ] || []
                                                    ).length === 0;
                                                }
                                            ) ? (
                                                <div
                                                    className="fantasy-match-modal__no-results"
                                                >
                                                    No hay estadísticas
                                                    Fantasy registradas.
                                                </div>
                                            ) : null}
                                        </section>
                                    );
                                })}
                            </section>
                        )}
                    </>
                ) : null}
            </IonContent>
        </IonModal>
    );
};

export default FantasyMatchModal;
